import { useMutation } from "@tanstack/react-query"

import supabase from "@/supabase"

// Bank Details submission data
interface BankDetailsSubmissionData {
  applicationId: string
  bankName: string
  accountNumber: string
  sortCode: string
  accountName: string
  bankProofUrl?: string
}

// Identity confirmation submission data
interface IdentitySubmissionData {
  applicationId: string
  firstName: string
  lastName: string
  dateOfBirth: string
  sex: string
}

// Next of Kin submission data
interface NextOfKinSubmissionData {
  applicationId: string
  name: string
  phone: string
  relation: string
}

// Tax Details submission data
interface TaxDetailsSubmissionData {
  applicationId: string
  nationalInsuranceNumber: string
  utrNumber?: string
  companyType: string
  companyNumber?: string
  vatNumber?: string
}

// Address Details submission data
interface AddressDetailsSubmissionData {
  applicationId: string
  addressLine1: string
  addressLine2?: string
  addressCity: string
  addressPostcode: string
}

// Headshot submission data
interface HeadshotSubmissionData {
  applicationId: string
  headshotUrl: string
}

// Final Forms submission data
interface FinalFormsSubmissionData {
  applicationId: string
  contractPdfUrl?: string
  personalDetailsCompleted?: boolean
}

// Common response interface
interface SubmissionResponse {
  success: boolean
  message?: string
  data?: Record<string, unknown>
}

export const usePersonalDetailsCloudFunctions = () => {
  // Submit Identity Confirmation to cloud function
  const submitIdentity = useMutation({
    mutationFn: async (data: IdentitySubmissionData): Promise<SubmissionResponse> => {
      const { data: response, error } = await supabase.functions.invoke(
        "personal-details-submit-identity-confirmation",
        {
          body: data,
        },
      )

      if (error) {
        console.error("Error submitting identity confirmation:", error)
        try {
          const parsedError = await error.context.json()
          return parsedError
        } catch {
          return {
            success: false,
            message: error.message || "Failed to submit identity confirmation",
          }
        }
      }

      return response
    },
  })

  // Submit Bank Details to cloud function
  const submitBankDetails = useMutation({
    mutationFn: async (data: BankDetailsSubmissionData): Promise<SubmissionResponse> => {
      const { data: response, error } = await supabase.functions.invoke(
        "personal-details-submit-bank-details",
        {
          body: data,
        },
      )

      if (error) {
        console.error("Error submitting bank details:", error)
        try {
          // Try to parse the error response body to get the actual error message
          const parsedError = await error.context.json()
          return parsedError
        } catch {
          // If parsing fails, fall back to error.message or default message
          return {
            success: false,
            message: error.message || "Failed to submit bank details",
          }
        }
      }

      return response
    },
  })

  // Submit Next of Kin information to cloud function
  const submitNextOfKin = useMutation({
    mutationFn: async (data: NextOfKinSubmissionData): Promise<SubmissionResponse> => {
      const { data: response, error } = await supabase.functions.invoke(
        "personal-details-submit-next-of-kin",
        {
          body: data,
        },
      )

      if (error) {
        console.error("Error submitting next of kin information:", error)
        try {
          // Try to parse the error response body to get the actual error message
          const parsedError = await error.context.json()
          return parsedError
        } catch {
          // If parsing fails, fall back to error.message or default message
          return {
            success: false,
            message: error.message || "Failed to submit next of kin information",
          }
        }
      }

      return response
    },
  })

  // Submit Tax Details to cloud function
  const submitTaxDetails = useMutation({
    mutationFn: async (data: TaxDetailsSubmissionData): Promise<SubmissionResponse> => {
      const { data: response, error } = await supabase.functions.invoke(
        "personal-details-submit-tax-details",
        {
          body: data,
        },
      )

      if (error) {
        console.error("Error submitting tax details:", error)
        try {
          // Try to parse the error response body to get the actual error message
          const parsedError = await error.context.json()
          return parsedError
        } catch {
          // If parsing fails, fall back to error.message or default message
          return {
            success: false,
            message: error.message || "Failed to submit tax details",
          }
        }
      }

      return response
    },
  })

  // Submit Address Details to cloud function
  const submitAddressDetails = useMutation({
    mutationFn: async (data: AddressDetailsSubmissionData): Promise<SubmissionResponse> => {
      const { data: response, error } = await supabase.functions.invoke(
        "personal-details-submit-address-details",
        {
          body: data,
        },
      )

      if (error) {
        console.error("Error submitting address details:", error)
        try {
          // Try to parse the error response body to get the actual error message
          const parsedError = await error.context.json()
          return parsedError
        } catch {
          // If parsing fails, fall back to error.message or default message
          return {
            success: false,
            message: error.message || "Failed to submit address details",
          }
        }
      }

      return response
    },
  })

  // Submit Headshot to cloud function
  const submitHeadshot = useMutation({
    mutationFn: async (data: HeadshotSubmissionData): Promise<SubmissionResponse> => {
      const { data: response, error } = await supabase.functions.invoke(
        "personal-details-submit-headshot",
        {
          body: data,
        },
      )

      if (error) {
        console.error("Error submitting headshot:", error)
        try {
          // Try to parse the error response body to get the actual error message
          const parsedError = await error.context.json()
          return parsedError
        } catch {
          // If parsing fails, fall back to error.message or default message
          return {
            success: false,
            message: error.message || "Failed to submit headshot",
          }
        }
      }

      return response
    },
  })

  // Submit Final Forms to cloud function
  const submitFinalForms = useMutation({
    mutationFn: async (data: FinalFormsSubmissionData): Promise<SubmissionResponse> => {
      const { data: response, error } = await supabase.functions.invoke(
        "personal-details-submit-final-forms",
        {
          body: data,
        },
      )

      if (error) {
        console.error("Error submitting final forms:", error)
        try {
          // Try to parse the error response body to get the actual error message
          const parsedError = await error.context.json()
          return parsedError
        } catch {
          // If parsing fails, fall back to error.message or default message
          return {
            success: false,
            message: error.message || "Failed to submit final forms",
          }
        }
      }

      return response
    },
  })

  return {
    submitIdentity,
    submitBankDetails,
    submitNextOfKin,
    submitTaxDetails,
    submitAddressDetails,
    submitHeadshot,
    submitFinalForms,
  }
}
